// Tiny i18n — no external dependency, no Suspense, no namespaces.
//
// Why so minimal: the app is mostly already in English, contractors
// largely speak English, and the "Spanish for crews" feature is for
// the FIELD employee — a few dozen labels on EmployeeHome + a half-
// dozen action buttons. A full i18n library (with pluralization,
// ICU MessageFormat, lazy bundles) is overkill.
//
// Translations are generated by piping en.json through Claude
// offline (npm run translate:es) — no API cost at runtime, no
// human-translation fee. Re-run when en.json grows.
//
// Usage:
//   const t = useT();
//   <button>{t("clock_in")}</button>
//
// Adding a string: drop it into en.json, run `npm run translate:es`,
// commit both. Missing keys render the raw key (visible bug, easy fix).

import {
  createContext,
  createElement,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";

export type Locale = "en" | "es";

const STORAGE_KEY = "fk_locale";

interface I18nContext {
  locale: Locale;
  setLocale: (l: Locale) => void;
  t: (key: string, vars?: Record<string, string | number>) => string;
}

const ctx = createContext<I18nContext>({
  locale: "en",
  setLocale: () => {},
  t: (k) => k,
});

// Lazy-loaded translation dictionaries. We bundle en at module load
// (zero-cost; it's also the source of truth) and dynamic-import es
// on first switch so users who never change locale don't pay for it.
import en from "../locales/en.json";

let esCache: Record<string, string> | null = null;
async function loadEs(): Promise<Record<string, string>> {
  if (esCache) return esCache;
  const mod = await import("../locales/es.json");
  esCache = (mod.default ?? mod) as Record<string, string>;
  return esCache;
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(
    () =>
      ((typeof localStorage !== "undefined" &&
        localStorage.getItem(STORAGE_KEY)) as Locale | null) || "en",
  );
  const [esLoaded, setEsLoaded] = useState(false);

  useEffect(() => {
    if (locale === "es" && !esLoaded) {
      void loadEs().then(() => setEsLoaded(true));
    }
  }, [locale, esLoaded]);

  function setLocale(l: Locale) {
    setLocaleState(l);
    if (typeof localStorage !== "undefined")
      localStorage.setItem(STORAGE_KEY, l);
    // Hint to the browser + assistive tech.
    if (typeof document !== "undefined") {
      document.documentElement.lang = l;
    }
  }

  function t(key: string, vars?: Record<string, string | number>): string {
    let str: string;
    if (locale === "es" && esCache) {
      str = esCache[key] ?? (en as Record<string, string>)[key] ?? key;
    } else {
      str = (en as Record<string, string>)[key] ?? key;
    }
    if (vars) {
      for (const [k, v] of Object.entries(vars)) {
        str = str.replace(new RegExp(`{${k}}`, "g"), String(v));
      }
    }
    return str;
  }

  return createElement(
    ctx.Provider,
    { value: { locale, setLocale, t } },
    children,
  );
}

export function useT(): I18nContext["t"] {
  return useContext(ctx).t;
}

export function useLocale(): {
  locale: Locale;
  setLocale: (l: Locale) => void;
} {
  const { locale, setLocale } = useContext(ctx);
  return { locale, setLocale };
}
